import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { type PaletteDef, typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { TextRange } from '@gitbook/slate';
import type { MarkProps } from '@gitbook/slate-react';
import { useLiveRef, useTimeout } from '@gitbook/util-react-hooks';

import { removeMarkFromSelection } from '../type-plugin';

/**
 * Component to render around a temporary mark, for example when typing ":" or "@".
 */
export function SelectionPalettePicker<PickerMarkData extends { value: string }>({
    ref,
    ...props
}: (MarkProps & {
    emptyPlaceholder?: React.ReactNode;
    getPalette: (markData: PickerMarkData, getCurrentRange: () => TextRange) => PaletteDef;
    withEditorInput?: boolean;
    withQuery?: boolean;
}) & { ref?: React.Ref<HTMLSpanElement> }) {
    const {
        mark,
        node,
        offset,
        attributes,
        children,
        emptyPlaceholder = 'Filter…',
        editor,
        getPalette,
        withEditorInput = true,
        withQuery = true,
    } = props;

    const markData = mark.data.toObject<PickerMarkData>();
    const { value } = markData;

    const anchorRef = useObjectRef(ref);
    const lastestValueRef = useLiveRef(value);
    const paletteHandle = usePaletteHandle(anchorRef, {
        onClose: () => {
            editor.change((change) => removeMarkFromSelection(change, mark.type).focus());
        },
        inputRef: withEditorInput ? editor.elementRef : undefined,
    });

    useTimeout(() => {
        /**
         * Open the palette only if the editor is focused otherwise if the editor re-renders
         * and the latest selection is around the "@" mark, the palette will be opened unexpectedly.
         */
        if (!paletteHandle.opened && editor.value.isFocused) {
            const getCurrentRange = () =>
                TextRange.create({
                    anchorKey: node.key,
                    anchorOffset: offset,
                    focusKey: node.key,
                    focusOffset: offset + lastestValueRef.current.length + 1,
                });

            const palette = getPalette(markData, getCurrentRange);
            paletteHandle.open(withQuery ? typeQueryInPalette(palette, value) : palette);
        }
    }, 150);

    // When the user types in the editor, we filter the mentions
    React.useEffect(() => {
        if (withQuery && paletteHandle.opened) {
            paletteHandle.typeQuery(value);
        }
    }, [paletteHandle, value, withQuery]);

    return (
        <span
            ref={anchorRef}
            className="rounded-sm bg-[rgba(0,0,0,0.05)] pr-1 outline outline-2 outline-[rgba(0,0,0,0.05)]"
            {...attributes}
        >
            {children}
            {value === '' ? (
                <span contentEditable={false} className="text-light">
                    {emptyPlaceholder}
                </span>
            ) : null}
        </span>
    );
}
